import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { credentialsForEmbeddingProfile } from "../../helpers/knowledgeManagement";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 18 },
  fields: { display: "flex", flexDirection: "column", gap: theme.spacing(2), minWidth: 480 },
  hint: { padding: theme.spacing(1.5), borderRadius: 10, backgroundColor: theme.palette.action.hover },
}));

const initial = {
  name: "",
  description: "",
  purpose: "customer_service",
  embeddingProfileId: "",
  credentialId: "",
  revision: undefined,
};

const purposes = [
  ["customer_service", "Atendimento ao cliente"],
  ["support", "Suporte"],
  ["sales", "Vendas"],
  ["faq", "Perguntas frequentes"],
  ["general", "Conhecimento geral"],
];

const KnowledgeBaseModal = ({
  open,
  onClose,
  onSaved,
  onConfigureCredentials,
  knowledgeBase,
  profiles,
  credentials,
}) => {
  const classes = useStyles();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const recommended = profiles.find((profile) => profile.recommended && profile.availableForNewUse)
      || profiles.find((profile) => profile.availableForNewUse);
    const profileId = knowledgeBase?.embeddingProfileId || recommended?.id || "";
    const profile = profiles.find((item) => Number(item.id) === Number(profileId));
    const compatible = credentialsForEmbeddingProfile(credentials, profile);
    setForm({
      ...initial,
      name: knowledgeBase?.name || "",
      description: knowledgeBase?.description || "",
      purpose: knowledgeBase?.purpose || "customer_service",
      embeddingProfileId: profileId,
      credentialId: knowledgeBase?.credentialId || compatible[0]?.id || "",
      revision: knowledgeBase?.revision,
    });
  }, [credentials, knowledgeBase, open, profiles]);

  const selectedProfile = useMemo(
    () => profiles.find((profile) => Number(profile.id) === Number(form.embeddingProfileId)),
    [form.embeddingProfileId, profiles]
  );
  const compatibleCredentials = useMemo(
    () => credentialsForEmbeddingProfile(credentials, selectedProfile),
    [credentials, selectedProfile]
  );

  const update = (field) => (event) => {
    const value = event.target.value;
    if (field === "embeddingProfileId") {
      const nextProfile = profiles.find((profile) => Number(profile.id) === Number(value));
      const nextCredentials = credentialsForEmbeddingProfile(credentials, nextProfile);
      setForm((current) => ({
        ...current,
        embeddingProfileId: value,
        credentialId: nextCredentials.some((item) => Number(item.id) === Number(current.credentialId))
          ? current.credentialId
          : (nextCredentials[0]?.id || ""),
      }));
      return;
    }
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        embeddingProfileId: Number(form.embeddingProfileId),
        credentialId: Number(form.credentialId),
      };
      const { data } = knowledgeBase?.id
        ? await api.put(`/knowledge-bases/${knowledgeBase.id}`, payload)
        : await api.post("/knowledge-bases", payload);
      toast.success(knowledgeBase?.id ? "Base atualizada." : "Base criada.");
      onSaved(data);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const valid = form.name.trim().length >= 3 && form.embeddingProfileId && form.credentialId;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
      <DialogTitle>{knowledgeBase?.id ? "Editar base de conhecimento" : "Nova base de conhecimento"}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.fields}>
          <TextField label="Nome" variant="outlined" value={form.name} onChange={update("name")} inputProps={{ maxLength: 160 }} required />
          <TextField label="Descrição" variant="outlined" value={form.description} onChange={update("description")} multiline rows={2} inputProps={{ maxLength: 2000 }} />
          <FormControl variant="outlined" required>
            <InputLabel>Finalidade</InputLabel>
            <Select value={form.purpose} onChange={update("purpose")} label="Finalidade">
              {purposes.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl variant="outlined" required>
            <InputLabel>Perfil de embeddings</InputLabel>
            <Select value={form.embeddingProfileId} onChange={update("embeddingProfileId")} label="Perfil de embeddings">
              {profiles.filter((profile) => profile.availableForNewUse || Number(profile.id) === Number(form.embeddingProfileId)).map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.code} · {profile.dimension} dimensões{profile.recommended ? " · recomendado" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" required>
            <InputLabel>Credencial de embeddings</InputLabel>
            <Select value={form.credentialId} onChange={update("credentialId")} label="Credencial de embeddings">
              {compatibleCredentials.map((credential) => (
                <MenuItem key={credential.id} value={credential.id}>
                  {credential.provider} · {credential.secretHint || "configurada"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!compatibleCredentials.length && (
            <div className={classes.hint}>
              <Typography variant="body2" color="error">
                Configure uma credencial de embeddings do provedor deste perfil antes de criar a base.
              </Typography>
              {onConfigureCredentials && (
                <Button
                  color="primary"
                  onClick={() => onConfigureCredentials({
                    purpose: "embedding",
                    provider: selectedProfile?.model?.provider,
                  })}
                >
                  Configurar credencial
                </Button>
              )}
            </div>
          )}
          {knowledgeBase?.sourceCount > 0 && (
            <Typography variant="body2" color="textSecondary" className={classes.hint}>
              O perfil de embeddings não pode ser trocado enquanto a base possuir fontes.
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={save} disabled={!valid || saving}>
          {saving ? <CircularProgress size={22} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KnowledgeBaseModal;
